import React, { useState, useEffect, useRef } from 'react';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ISMExtractor() {
  const [status, setStatus] = useState('idle'); // idle, waiting_login, extracting, done, error
  const [pages, setPages] = useState('');
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    return () => stopPolling();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('notes_token') || '';
      const resp = await fetch(`${API_URL}/api/ism/status`, {
        headers: { 'x-token': token },
      });
      const data = await resp.json();
      if (data.success) {
        // 兼容后端返回的completed状态
        const s = data.status === 'completed' ? 'done' : data.status;
        setStatus(s);
        setProgress(data.progress || 0);
        setTotal(data.total || 0);
        setMessage(data.message || '');
        if (data.status === 'waiting_login' && data.qrReady) {
          setQrUrl(`${API_URL}/api/ism/qrcode?t=${Date.now()}`);
        }
      }
    } catch (e) {}
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(fetchStatus, 2000);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handleStart = async () => {
    const totalPages = parseInt(pages);
    if (!totalPages || totalPages < 1) {
      setError('请输入有效页数');
      return;
    }
    setError('');

    const token = localStorage.getItem('notes_token') || '';
    try {
      const resp = await fetch(`${API_URL}/api/ism/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        body: JSON.stringify({ pages: totalPages }),
      });
      const data = await resp.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      startPolling();
    } catch (e) {
      setError('启动失败: ' + e.message);
    }
  };

  const handleCancel = async () => {
    const token = localStorage.getItem('notes_token') || '';
    try {
      await fetch(`${API_URL}/api/ism/cancel`, {
        method: 'POST',
        headers: { 'x-token': token },
      });
      stopPolling();
      fetchStatus();
    } catch (e) {}
  };

  const percent = total > 0 ? Math.round((progress / total) * 100) : 0;

  return (
    <div className="ism-container">
      <div className="ism-header">
        <h2>📚 ISM知识库提取</h2>
        <p className="ism-desc">自动提取用友ISM智能服务云平台的知识条目</p>
      </div>

      {/* 空闲状态 - 输入页数 */}
      {status === 'idle' && (
        <div className="ism-card">
          <div className="ism-icon-wrap">
            <span className="ism-big-icon">📚</span>
          </div>
          <h3>开始新提取</h3>
          <p>输入需要提取的页数，系统将自动登录并提取知识条目</p>
          
          <div className="ism-input-group">
            <label>提取页数</label>
            <div className="ism-input-row">
              <input
                type="number"
                value={pages}
                onChange={e => setPages(e.target.value)}
                placeholder="例如：100"
                min="1"
                max="10000"
                disabled={status !== 'idle'}
              />
              <span className="ism-input-suffix">页</span>
            </div>
            <span className="ism-hint">建议首次测试输入 10 页，确认正常后再大批量提取</span>
          </div>

          {error && <div className="ism-error">{error}</div>}

          <button className="btn-ism-start" onClick={handleStart}>
            🚀 开始提取
          </button>
        </div>
      )}

      {/* 等待登录 - 显示二维码 */}
      {status === 'waiting_login' && (
        <div className="ism-card ism-login-card">
          <div className="ism-qr-section">
            <h3>📱 请扫描二维码登录</h3>
            <p>打开微信扫一扫，扫描下方二维码</p>
            {qrUrl && (
              <div className="ism-qr-wrap">
                <img src={qrUrl} alt="登录二维码" className="ism-qr-img" />
              </div>
            )}
            <div className="ism-login-hint">
              <span className="ism-pulse-dot" />
              等待扫码中...
            </div>
          </div>

          <div className="ism-extract-info">
            <div className="ism-info-row">
              <span className="ism-info-label">计划页数</span>
              <span className="ism-info-value">{total} 页</span>
            </div>
          </div>

          <button className="btn-ism-cancel" onClick={handleCancel}>
            取消
          </button>
        </div>
      )}

      {/* 提取中 - 显示进度 */}
      {status === 'extracting' && (
        <div className="ism-card">
          <div className="ism-extracting-header">
            <div className="ism-spinner" />
            <div>
              <h3>正在提取中</h3>
              <p className="ism-sub-message">{message}</p>
            </div>
          </div>

          <div className="ism-progress-section">
            <div className="ism-progress-bar-wrap">
              <div className="ism-progress-bar" style={{ width: `${percent}%` }} />
            </div>
            <div className="ism-progress-text">
              <span>{progress} / {total} 页</span>
              <span>{percent}%</span>
            </div>
          </div>

          <div className="ism-extract-info">
            <div className="ism-info-row">
              <span className="ism-info-label">进度</span>
              <span className="ism-info-value">{progress} / {total} 页</span>
            </div>
            <div className="ism-info-row">
              <span className="ism-info-label">完成率</span>
              <span className="ism-info-value">{percent}%</span>
            </div>
            <div className="ism-info-row">
              <span className="ism-info-label">预计剩余</span>
              <span className="ism-info-value">
                {progress > 0 ? `约 ${Math.ceil((total - progress) * 2 / 60)} 分钟` : '计算中...'}
              </span>
            </div>
          </div>

          <button className="btn-ism-cancel" onClick={handleCancel}>
            取消提取
          </button>
        </div>
      )}

      {/* 完成 */}
      {status === 'done' && (
        <div className="ism-card ism-done-card">
          <div className="ism-done-icon">✅</div>
          <h3>提取完成</h3>
          <p>共提取 {progress} 页知识条目</p>
          
          <div className="ism-extract-info">
            <div className="ism-info-row">
              <span className="ism-info-label">提取页数</span>
              <span className="ism-info-value">{progress} 页</span>
            </div>
            <div className="ism-info-row">
              <span className="ism-info-label">完成时间</span>
              <span className="ism-info-value">
                {new Date().toLocaleString('zh-CN')}
              </span>
            </div>
            <div className="ism-info-row">
              <span className="ism-info-label">保存位置</span>
              <span className="ism-info-value">knowledge/ISM/</span>
            </div>
          </div>

          <button className="btn-ism-start" onClick={() => { setStatus('idle'); setProgress(0); setTotal(0); setPages(''); }}>
            🔄 再次提取
          </button>
        </div>
      )}

      {/* 错误 */}
      {status === 'error' && (
        <div className="ism-card ism-error-card">
          <div className="ism-error-icon">⚠️</div>
          <h3>提取失败</h3>
          <p>{message}</p>
          <button className="btn-ism-start" onClick={() => { setStatus('idle'); setProgress(0); setTotal(0); setPages(''); setError(''); }}>
            🔄 重试
          </button>
        </div>
      )}
    </div>
  );
}
